import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OnboardingLink, OnboardingLinkAddon, OnboardingLinkMetadata } from 'voxloud-types/dist/models';
import { Subject } from 'rxjs';

import { environment } from 'src/environments/environment';
import { CopyFeedbackPopupComponent } from 'src/app/shared/components';
import { ActivateFormValue, ExtendedOnboardingLinkAddon, SetupFee, SetupFeeInternalId } from '../../models/activate-link.model';
import { PAYMENT_METHODS_MAP } from 'src/app/domains/sales/activation-link/models/activation-link.model';
import { ActivationLinkService } from 'src/app/domains/sales/activation-link/services';
import { REQUIRED_ADDONS, SETUP_FEES } from '../../constant/activation-link.constant';
import { AuthFacade } from 'src/app/domains/auth/store/facade/auth.facade';
import { take } from 'rxjs/operators';
import { atLeastOneMoreThanZero, calculateExpiryDate } from 'src/app/domains/sales/activation-link/utils/activation-link-utils';

@Component({
  selector: 'gdp-activation-link-unlimited-voice',
  templateUrl: './activation-link-unlimited-voice.component.html',
  styleUrls: ['./activation-link-unlimited-voice.component.scss']
})
export class ActivationLinkUnlimitedVoiceComponent implements OnInit, OnDestroy {
  public modalContent = '';
  public loading: boolean = false;
  public loadingSubmit: boolean = false;
  public addonsError: boolean = false;
  public optionalAddons: ExtendedOnboardingLinkAddon[] = [];
  private planId: string = 'it-unlimited_professionale-v1';
  private destroy$ = new Subject<void>();
  private currentUser: string = '';

  private validators = REQUIRED_ADDONS.map((v: string) => atLeastOneMoreThanZero(v));

  public setupFeeOptions = SETUP_FEES.filter((v) => v.id !== SetupFeeInternalId.Accelerator);

  public form: FormGroup = new FormGroup({
    activationData: new FormControl<ActivateFormValue>(null),
    requiredAddons: new FormArray([], this.validators),
    optionalAddons: new FormControl([]),
    setupFee: new FormControl(this.setupFeeOptions[0])
  });

  @ViewChild('activationUnlimitedVoiceLinkPopup', { static: false }) linkPopup: CopyFeedbackPopupComponent;

  constructor(
    private modalService: NgbModal,
    private activationLinkService: ActivationLinkService,
    private authFacade: AuthFacade
  ) { }

  ngOnInit() {
    this.authFacade.username$.pipe(take(1)).subscribe((username: string) => {
      this.currentUser = username;
    });
    this.loadAddons();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAddons(): void {
    this.loading = true;
    this.addonsError = false;
    this.activationLinkService.getAddonsByPlan(this.planId).subscribe({
      next: (addons) => {
        // Populate required addons dynamically
        const requiredAddonsArray = this.form.get('requiredAddons') as FormArray;
        this.optionalAddons = [];
        const requiredAddons = [];
        requiredAddonsArray.clear();


        addons.forEach((a) => {
          const addon: ExtendedOnboardingLinkAddon = {
            id: a.id,
            min: 0,
            max: a.pricing_model === 'flat_fee' ? 1 : undefined,
            name: a.name,
            quantity: 0
          }
          const includeAddon = REQUIRED_ADDONS.some((v) => addon.id.includes(v));
          if(includeAddon){
            requiredAddons.push(addon);
          } else {
            this.optionalAddons.push(addon);
          }
        });

        requiredAddons.sort((a: ExtendedOnboardingLinkAddon, b: ExtendedOnboardingLinkAddon) => a.name.localeCompare(b.name));
        this.optionalAddons.sort((a: ExtendedOnboardingLinkAddon, b: ExtendedOnboardingLinkAddon) => a.name.localeCompare(b.name));
        requiredAddons.forEach((v) => {
          requiredAddonsArray.push(new FormControl(v));
        });

        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.addonsError = true;
      }
    });
  }

  public retryLoadAddons(): void {
    this.loadAddons();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      this.loadingSubmit = true;
      const activationData = this.form.get('activationData').value;
      const setupFee: SetupFee = this.form.get('setupFee').value;
      const expiryDate = calculateExpiryDate(activationData);
      const data: OnboardingLinkMetadata = {
        name: activationData.name,
        expires_at: expiryDate.toISOString(),
        deal_id: activationData.deal_id,
        payment_methods: PAYMENT_METHODS_MAP[activationData.paymentMethod],
        sms: activationData.sms,
        plan_id: this.planId,
        coupon: null,
        setup_fee: {
          price: setupFee.amount,
          name: setupFee.name
        },
        addons: this.getAddons()
      }
      this.activationLinkService.saveOnboardingData(
        this.currentUser,
        expiryDate.toISOString(),
        JSON.stringify(data),
      ).subscribe(
        (res: OnboardingLink) => {
          this.loadingSubmit = false;
          this.modalService.open(this.linkPopup, { size: 'lg' });
          const params = `?id=${res.id}&country=${activationData.country.iso2.toUpperCase()}`;
          this.modalContent = `${environment.activationLink.unlimitedUsersProfessionale}${params}`
        },
        () => {
          this.loadingSubmit = false;
        }
      );
    }
  }

  private getAddons() {
    const reqAddons = this.form.get('requiredAddons').value;
    const optionalAddons = this.form.get('optionalAddons').value;
    return [...reqAddons.filter((v: ExtendedOnboardingLinkAddon) => v.quantity > 0).map((v: ExtendedOnboardingLinkAddon) => {
      return {
        id: v.id,
        quantity: v.quantity
      }
    }), ...optionalAddons.filter((v: ExtendedOnboardingLinkAddon) => v.quantity > 0)];
  }

  get requiredAddonsControls() {
    return (this.form.get('requiredAddons') as FormArray).controls;
  }
}
